/**
 * Форматирование общих блоков отчётов (kpi, график, воронка, таблица).
 *
 * Формат блоков задаёт сервис аналитики (analytics-hub, internal/view):
 * Amplitude, а дальше Метрика и GA4 приходят в одном виде, поэтому
 * и форматирование одно на все системы.
 */

import {formatCount} from "@/common/rumFormat";

/** Изменение меньше этого — шум, без цвета. */
const NOISE = 0.03

/**
 * Значение по единице блока: "" — штуки, "%" — доля 0..100, "s" — секунды.
 */
export function formatValue(value, unit = '') {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
    if (unit === '%') return formatPercentValue(value)
    if (unit === 's') return formatDuration(value)

    return formatCount(Math.round(value))
}

/** Доля 0..100: «70%», малые — с десятой: «2.5%». */
export function formatPercentValue(value) {
    const digits = value > 0 && value < 10 ? 1 : 0

    return `${Number(value).toFixed(digits).replace(/\.0$/, '')}%`
}

/** Доля 0..1 из воронки: «73%». */
export function formatShare(share) {
    if (share === null || share === undefined) return '—'

    return formatPercentValue(share * 100)
}

export function formatDuration(sec) {
    if (sec === null || sec === undefined) return '—'
    if (sec < 1) return '< 1 с'
    if (sec < 60) return `${Math.round(sec)} с`
    if (sec < 3600) return `${Math.round(sec / 60)} мин`

    return `${(sec / 3600).toFixed(1).replace(/\.0$/, '')} ч`
}

/**
 * Изменение к прошлому периоду. Для долей — в процентных пунктах: «доля
 * новых выросла на 15%» с 70% читается как 85%, а это 80.5%.
 *
 * direction: better/worse — для подсветки, null — шум или не с чем сравнить.
 * Больше — лучше для всех чисел, кроме долей в kpi: «доля новых» не бывает
 * хорошей или плохой сама по себе, её не красим (neutral). Для ошибок рост
 * плохой — сервис помечает такие числа lowerIsBetter.
 */
export function formatDelta(value, prev, {unit = '', neutral = false, lowerIsBetter = false} = {}) {
    const delta = rawDelta(value, prev, unit, neutral)
    if (lowerIsBetter && delta && delta.direction) {
        delta.direction = delta.direction === 'better' ? 'worse' : 'better'
    }

    return delta
}

function rawDelta(value, prev, unit, neutral) {
    if (prev === null || prev === undefined || value === null || value === undefined) return null

    if (unit === '%') {
        const pp = value - prev
        if (Math.abs(pp) < 0.05) return {text: 'без изменений', direction: null}
        const sign = pp > 0 ? '+' : '−'

        return {
            text: `${sign}${Math.abs(pp).toFixed(1).replace(/\.0$/, '')} п.п.`,
            direction: neutral || Math.abs(pp) < NOISE * 100 ? null : (pp > 0 ? 'better' : 'worse'),
        }
    }

    // С нуля процент не посчитать — «+∞%» никому не поможет.
    if (!prev) return null
    const rel = (value - prev) / prev
    if (Math.round(rel * 100) === 0) return {text: 'без изменений', direction: null}
    const sign = rel > 0 ? '+' : '−'

    return {
        text: `${sign}${Math.abs(Math.round(rel * 100))}%`,
        direction: neutral || Math.abs(rel) < NOISE ? null : (rel > 0 ? 'better' : 'worse'),
    }
}

/** «2026-10-27» → «27.10». */
export function formatDay(iso) {
    if (!iso) return ''
    const [, m, d] = iso.split('-')

    return `${d}.${m}`
}

/** Период отчёта: «27.10 – 02.11» или «02.11» за один день. */
export function formatRange(from, to) {
    if (!from) return ''
    if (from === to || !to) return formatDay(from)

    return `${formatDay(from)} – ${formatDay(to)}`
}

/**
 * Подпись точки на оси: день «27.10» или час «13:00». Часы приходят как
 * «2026-11-02T13:00:00» — в часовом поясе проекта Amplitude, переводить
 * в местное не нужно.
 */
export function formatAxisPoint(x) {
    if (!x) return ''
    if (x.length > 10) return x.slice(11, 16)

    return formatDay(x)
}
